import { idb } from "../db/index.ts";
import { g, helpers } from "../util/index.ts";
import type { TeamSeason, Player } from "../../../common/types.ts";
import { getBestPos } from "../core/player/checkJerseyNumberRetirement.ts";
import addFirstNameShort from "../util/addFirstNameShort.ts";
import { getPlayoffsByConfBySeason } from "./teamSeasonsShared.ts";

// M3-1: the standalone team-history page was retired in favour of a
// team-history MobileSheet (reachable from the roster/team tab for the user's
// own team AND for any scouted opponent). The franchise-history logic that used
// to live in the `teamHistory` view handler now lives here as shared functions:
//   - getHistoryTeam / getHistory: still consumed by the roster server view
//     (opponent franchise-summary row) and by the sheet.
//   - getTeamHistorySheet: assembles the compact sheet payload fetched ad hoc
//     via the `getTeamHistorySheet` worker RPC.

type PlayoffsByConfBySeason = Awaited<
	ReturnType<typeof getPlayoffsByConfBySeason>
>;

export const getHistoryTeam = (
	teamSeasons: TeamSeason[],
	playoffsByConfBySeason: PlayoffsByConfBySeason,
) => {
	let bestRecord;
	let worstRecord;
	let bestWinp = -Infinity;
	let worstWinp = Infinity;

	const history: {
		season: number;
		won: number;
		lost: number;
		tied?: number;
		otl?: number;
		playoffRoundsWon: number;
		numPlayoffRounds: number;
		roundsWonText: string;
		region?: string;
		name?: string;
		tid: number;
		abbrev: string;
	}[] = [];

	let totalWon = 0;
	let totalLost = 0;
	let totalTied = 0;
	let totalOtl = 0;
	let playoffAppearances = 0;
	let finalsAppearances = 0;
	let championships = 0;
	let lastChampionship = undefined;

	for (const teamSeason of teamSeasons) {
		const numPlayoffRounds = g.get(
			"numGamesPlayoffSeries",
			teamSeason.season,
		).length;

		history.push({
			season: teamSeason.season,
			won: teamSeason.won,
			lost: teamSeason.lost,
			tied: teamSeason.tied,
			otl: teamSeason.otl,
			playoffRoundsWon: teamSeason.playoffRoundsWon,
			roundsWonText: helpers.roundsWonText({
				playoffRoundsWon: teamSeason.playoffRoundsWon,
				numPlayoffRounds,
				playoffsByConf: playoffsByConfBySeason.get(teamSeason.season),
			}),
			numPlayoffRounds,
			region: teamSeason.region || undefined,
			name: teamSeason.name || undefined,
			tid: teamSeason.tid,
			abbrev:
				teamSeason.abbrev || g.get("teamInfoCache")[teamSeason.tid]!.abbrev,
		});
		totalWon += teamSeason.won;
		totalLost += teamSeason.lost;
		totalTied += teamSeason.tied;
		totalOtl += teamSeason.otl;

		if (teamSeason.playoffRoundsWon >= 0) {
			playoffAppearances += 1;
		}

		if (
			teamSeason.playoffRoundsWon >= numPlayoffRounds - 1 &&
			teamSeason.playoffRoundsWon >= 0
		) {
			finalsAppearances += 1;
		}

		if (teamSeason.playoffRoundsWon === numPlayoffRounds) {
			championships += 1;
			lastChampionship = teamSeason.season;
		}

		const gp = helpers.getTeamSeasonGp(teamSeason);
		if (gp > 0) {
			const winp = helpers.calcWinp(teamSeason);

			if (
				!bestRecord ||
				(winp >= bestWinp &&
					(teamSeason.won > bestRecord.won ||
						teamSeason.lost < bestRecord.lost))
			) {
				bestRecord = history.at(-1);
				bestWinp = winp;
			}

			if (
				!worstRecord ||
				(winp <= worstWinp &&
					(teamSeason.lost > worstRecord.lost ||
						teamSeason.won < worstRecord.won))
			) {
				worstRecord = history.at(-1);
				worstWinp = winp;
			}
		}
	}

	history.reverse(); // Show most recent season first

	const totalWinp = helpers.calcWinp({
		won: totalWon,
		lost: totalLost,
		tied: totalTied,
		otl: totalOtl,
	});

	return {
		history,
		totalWon,
		totalLost,
		totalTied,
		totalOtl,
		totalWinp,
		playoffAppearances,
		finalsAppearances,
		championships,
		bestRecord,
		worstRecord,
		lastChampionship,
	};
};

export const getHistory = async (
	teamSeasons: TeamSeason[],
	playersAll: Player[],
	playoffsByConfBySeason: PlayoffsByConfBySeason,
	gmHistory?: boolean,
) => {
	const teamHistory = getHistoryTeam(teamSeasons, playoffsByConfBySeason);

	const stats = ["gp", "min", "pts", "trb", "ast", "per", "ewa"];

	let players = await idb.getCopies.playersPlus(playersAll, {
		attrs: [
			"pid",
			"firstName",
			"lastName",
			"injury",
			"tid",
			"hof",
			"watch",
			"jerseyNumber",
			"awards",
			"retirableJerseyNumbers",
		],
		ratings: ["pos", "season"],
		stats: ["season", "abbrev", ...stats],
	});

	// Not sure why this is necessary, but sometimes statsTids gets an entry but ratings doesn't
	players = players.filter((p) => p.careerStats.gp > 0);

	players = addFirstNameShort(players);

	const champSeasons = new Set(
		teamHistory.history
			.filter((row) => row.playoffRoundsWon >= row.numPlayoffRounds)
			.map((row) => row.season),
	);

	for (const p of players) {
		p.lastYr = "";
		if (p.stats.length > 0) {
			p.lastYr = p.stats.at(-1).season.toString();
			if (gmHistory) {
				p.lastYr += ` ${p.stats.at(-1).abbrev}`;
			}
		}

		p.numRings = p.awards.filter(
			(award: Player["awards"][number]) =>
				award.type === "Won Championship" && champSeasons.has(award.season),
		).length;
		delete p.awards;

		// undefined as 2nd argument because we have already filtered stats before getting here
		p.pos = getBestPos(p, undefined);

		delete p.ratings;
		delete p.stats;
	}

	return {
		...teamHistory,
		players,
		stats,
		userTid: g.get("userTid"),
	};
};

// --- MobileSheet payload (M3-1) ------------------------------------------

// Digestible slices for the sheet: the full page rendered season-by-season and
// career tables; the sheet keeps a compact top-N of each so it stays a glance,
// not a page. The return shape is an anonymous structural type (no exported
// alias) so the RPC codegen (tools/genApiTypes.ts) expands it inline in
// common/types/.gen/workerApi.d.ts rather than emitting an unresolved name.
const SHEET_RECENT_SEASON_LIMIT = 8;
const SHEET_RETIRED_JERSEY_LIMIT = 6;
const SHEET_LEADER_LIMIT = 5;

export const getTeamHistorySheet = async (tid: number) => {
	const t = await idb.cache.teams.get(tid);
	if (!t) {
		throw new Error("Invalid team ID number");
	}

	const teamSeasons = await idb.getCopies.teamSeasons({ tid }, "noCopyCache");

	const retiredJerseyNumbersRaw = await Promise.all(
		(t.retiredJerseyNumbers ?? []).map(async (row) => {
			let firstName: string | undefined;
			let lastName: string | undefined;
			if (row.pid !== undefined) {
				const p = await idb.getCopy.players({ pid: row.pid }, "noCopyCache");
				if (p) {
					firstName = p.firstName;
					lastName = p.lastName;
				}
			}

			return {
				number: row.number,
				firstName,
				lastName,
				pid: row.pid,
				seasonRetired: row.seasonRetired,
				text: row.text,
			};
		}),
	);

	const players = await idb.getCopies.players({ statsTid: tid });

	const playoffsByConfBySeason = await getPlayoffsByConfBySeason();
	const history = await getHistory(teamSeasons, players, playoffsByConfBySeason);

	// Franchise leaders: top-N by the primary career stat (points), keeping the
	// heavy DataTable off the sheet.
	const leaderStat = "pts";
	const leaders: {
		pid: number;
		firstName: string;
		firstNameShort?: string;
		lastName: string;
		pos?: string;
		value: number;
		numRings: number;
	}[] = [...history.players]
		.sort(
			(a: any, b: any) =>
				Number(b.careerStats?.[leaderStat] ?? 0) -
				Number(a.careerStats?.[leaderStat] ?? 0),
		)
		.slice(0, SHEET_LEADER_LIMIT)
		.map((p: any) => ({
			pid: p.pid as number,
			firstName: p.firstName as string,
			firstNameShort: p.firstNameShort as string | undefined,
			lastName: p.lastName as string,
			pos: p.pos as string | undefined,
			value: Number(p.careerStats?.[leaderStat] ?? 0),
			numRings: (p.numRings ?? 0) as number,
		}));

	const recentSeasons: {
		season: number;
		won: number;
		lost: number;
		tied?: number;
		otl?: number;
		tid: number;
		abbrev: string;
		roundsWonText: string;
		champion: boolean;
		madePlayoffs: boolean;
	}[] = history.history.slice(0, SHEET_RECENT_SEASON_LIMIT).map((row) => ({
		season: row.season,
		won: row.won,
		lost: row.lost,
		tied: row.tied,
		otl: row.otl,
		tid: row.tid,
		abbrev: row.abbrev,
		roundsWonText: row.roundsWonText,
		champion: row.playoffRoundsWon === row.numPlayoffRounds,
		madePlayoffs: row.playoffRoundsWon >= 0,
	}));

	const retiredJerseys: {
		number: string;
		firstName?: string;
		lastName?: string;
		pid?: number;
		seasonRetired: number;
		text?: string;
	}[] = [...retiredJerseyNumbersRaw]
		.sort((a, b) => b.seasonRetired - a.seasonRetired)
		.slice(0, SHEET_RETIRED_JERSEY_LIMIT)
		.map((row) => ({
			number: row.number,
			firstName: row.firstName,
			lastName: row.lastName,
			pid: row.pid,
			seasonRetired: row.seasonRetired,
			text: row.text,
		}));

	const bestRecord:
		| {
				season: number;
				won: number;
				lost: number;
				tied?: number;
				otl?: number;
				abbrev: string;
				tid: number;
		  }
		| undefined = history.bestRecord
		? {
				season: history.bestRecord.season,
				won: history.bestRecord.won,
				lost: history.bestRecord.lost,
				tied: history.bestRecord.tied,
				otl: history.bestRecord.otl,
				abbrev: history.bestRecord.abbrev,
				tid: history.bestRecord.tid,
			}
		: undefined;

	return {
		tid,
		abbrev: t.abbrev,
		season: g.get("season"),
		totalWon: history.totalWon,
		totalLost: history.totalLost,
		totalTied: history.totalTied,
		totalOtl: history.totalOtl,
		championships: history.championships,
		playoffAppearances: history.playoffAppearances,
		finalsAppearances: history.finalsAppearances,
		lastChampionship: history.lastChampionship as number | undefined,
		bestRecord,
		recentSeasons,
		retiredJerseys,
		leaderStat,
		leaders,
		teamColors: t.colors,
	};
};
